import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { useLanguage } from "@/LanguageContext";
import { CALENDLY_URL } from "@/i18n";
import { EASE } from "@/components/Reveal";

const ProjectModal = ({ project, onClose }) => {
  const { t } = useLanguage();

  useEffect(() => {
    if (!project) return;
    window.__lenis?.stop();
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      window.__lenis?.start();
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [project, onClose]);

  return (
    <AnimatePresence>
      {project && (
        <motion.div
          data-testid="project-modal"
          className="fixed inset-0 z-[90] flex items-end justify-center sm:items-center sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div
            data-testid="project-modal-backdrop"
            onClick={onClose}
            className="absolute inset-0 bg-noom-ink/60 backdrop-blur-sm"
          />
          <motion.div
            initial={{ y: 90, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.55, ease: EASE }}
            data-lenis-prevent
            className="relative z-10 max-h-[92vh] w-full max-w-4xl overflow-y-auto border border-noom-line bg-white"
          >
            <button
              data-testid="project-modal-close"
              onClick={onClose}
              aria-label="Chiudi scheda progetto"
              className="absolute right-4 top-4 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-noom-ink text-white transition-colors duration-300 hover:bg-noom-lilac hover:text-noom-ink"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="relative h-[38vh] min-h-[240px] w-full overflow-hidden bg-noom-paper">
              <img
                src={project.cover}
                alt={project.title}
                className={`h-full w-full ${project.contain ? "object-contain p-8" : "object-cover"}`}
              />
            </div>

            <div className="p-7 sm:p-10 lg:p-12">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-noom-lilacDeep" data-testid="project-modal-meta">
                {project.cat} · {project.meta}
              </p>
              <h3 className="mt-4 font-display text-3xl font-semibold tracking-tight text-noom-ink sm:text-4xl">
                {project.title}
              </h3>
              <p className="mt-5 max-w-2xl text-sm leading-relaxed text-noom-muted sm:text-base">{project.desc}</p>

              <p className="mt-9 font-mono text-[11px] uppercase tracking-[0.25em] text-noom-faint">
                {t.portfolio.deliverablesLabel}
              </p>
              <div className="mt-4 flex flex-wrap gap-2.5" data-testid="project-modal-deliverables">
                {project.deliverables.map((d, i) => (
                  <span
                    key={i}
                    className="rounded-full bg-noom-lilacLight px-4 py-2 text-xs font-medium text-noom-lilacDeep"
                  >
                    {d}
                  </span>
                ))}
              </div>

              <div className="mt-10 grid gap-4 sm:grid-cols-2">
                {project.gallery.map((g, i) => (
                  <figure
                    key={i}
                    className={project.gallery.length % 2 === 1 && i === 0 ? "sm:col-span-2" : ""}
                  >
                    <img
                      src={g.src}
                      alt={g.caption || project.title}
                      loading="lazy"
                      className="w-full border border-noom-line object-cover"
                    />
                    {g.caption && (
                      <figcaption className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-noom-faint">
                        {g.caption}
                      </figcaption>
                    )}
                  </figure>
                ))}
              </div>

              <div className="mt-10 border-t border-noom-line pt-8">
                <a
                  data-testid="project-modal-cta"
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="group inline-flex items-center gap-3 rounded-full bg-noom-ink px-7 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-noom-lilac hover:text-noom-ink"
                >
                  {t.footer.cta}
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProjectModal;
